import { adminTheme, mergeTheme } from '@crosscode/gravity-components'
import type { ThemeConfig } from 'antd'

/** Dispatch — inverted primary (zinc), status colors for success/error/info */
export const kitTokens = {
  colorPrimary: '#18181b',
  colorSuccess: '#16a34a',
  colorWarning: '#d97706',
  colorError: '#dc2626',
  colorInfo: '#2563eb',
  borderRadius: 6,
  fontSize: 13,
  fontFamily: '"Golos Text", "Onest", "Segoe UI", system-ui, sans-serif',
  colorBgLayout: '#fafafa',
  colorBgContainer: '#ffffff',
  colorTextBase: '#18181b',
  colorBorder: '#d4d4d8',
  controlHeight: 32,
  /** Text on solid primary (black btn → white text) */
  colorTextLightSolid: '#fafafa',
}

export const kitTokensDark = {
  ...kitTokens,
  colorPrimary: '#fafafa',
  colorSuccess: '#4ade80',
  colorWarning: '#fbbf24',
  colorError: '#f87171',
  colorInfo: '#60a5fa',
  colorBgLayout: '#09090b',
  colorBgContainer: '#18181b',
  colorTextBase: '#fafafa',
  colorBorder: '#3f3f46',
  /** Text on solid primary (white btn → dark text) */
  colorTextLightSolid: '#18181b',
}

const sharedComponents = {
  Button: {
    primaryShadow: 'none',
    dangerShadow: 'none',
    borderRadiusLG: 6,
    controlHeight: 32,
    fontWeight: 500,
  },
  Card: {
    borderRadiusLG: 8,
    paddingLG: 14,
  },
  Modal: {
    borderRadiusLG: 12,
  },
  Tag: {
    borderRadiusSM: 4,
  },
} as const

export const kitTheme: ThemeConfig = mergeTheme(adminTheme, {
  token: {
    colorPrimary: kitTokens.colorPrimary,
    colorPrimaryHover: '#27272a',
    colorSuccess: kitTokens.colorSuccess,
    colorWarning: kitTokens.colorWarning,
    colorError: kitTokens.colorError,
    colorInfo: kitTokens.colorInfo,
    borderRadius: kitTokens.borderRadius,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    fontSize: kitTokens.fontSize,
    fontFamily: kitTokens.fontFamily,
    colorBgLayout: kitTokens.colorBgLayout,
    colorBgContainer: kitTokens.colorBgContainer,
    colorBgElevated: '#ffffff',
    colorBgSpotlight: '#18181b',
    colorText: kitTokens.colorTextBase,
    colorTextSecondary: '#52525b',
    colorTextTertiary: '#a1a1aa',
    colorBorder: kitTokens.colorBorder,
    colorBorderSecondary: '#e4e4e7',
    colorTextLightSolid: kitTokens.colorTextLightSolid,
    controlItemBgHover: '#f4f4f5',
    controlItemBgActive: '#e4e4e7',
    controlHeight: kitTokens.controlHeight,
    motionDurationMid: '0.18s',
    motionEaseOut: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
    boxShadowSecondary: '0 16px 48px rgba(0, 0, 0, 0.12)',
  },
  components: {
    Layout: {
      bodyBg: '#fafafa',
      headerBg: '#fafafa',
      siderBg: '#fafafa',
      headerHeight: 48,
    },
    Menu: {
      itemBorderRadius: 6,
      itemSelectedBg: '#f4f4f5',
      itemSelectedColor: '#18181b',
      itemHoverBg: '#f4f4f5',
      itemHoverColor: '#18181b',
    },
    Table: {
      headerBg: 'transparent',
      rowHoverBg: '#f4f4f5',
      borderColor: '#e4e4e7',
      cellPaddingBlock: 10,
    },
    Input: {
      activeBorderColor: '#18181b',
      hoverBorderColor: '#a1a1aa',
      activeShadow: 'none',
    },
    Select: {
      optionSelectedBg: '#f4f4f5',
      optionActiveBg: '#f4f4f5',
    },
    Dropdown: {
      controlItemBgHover: '#f4f4f5',
      controlItemBgActive: '#e4e4e7',
    },
    Segmented: {
      itemSelectedBg: '#18181b',
      itemSelectedColor: '#fafafa',
      trackBg: '#f4f4f5',
    },
    Pagination: {
      itemActiveBg: '#18181b',
      itemBg: 'transparent',
      itemLinkBg: 'transparent',
      itemInputBg: '#ffffff',
    },
    Tooltip: {
      colorBgSpotlight: '#18181b',
      colorTextLightSolid: '#fafafa',
    },
    ...sharedComponents,
    Modal: {
      ...sharedComponents.Modal,
      contentBg: '#ffffff',
      headerBg: '#ffffff',
    },
  },
})

export const kitThemeDark: ThemeConfig = mergeTheme(adminTheme, {
  token: {
    colorPrimary: kitTokensDark.colorPrimary,
    colorPrimaryHover: '#e4e4e7',
    colorSuccess: kitTokensDark.colorSuccess,
    colorWarning: kitTokensDark.colorWarning,
    colorError: kitTokensDark.colorError,
    colorInfo: kitTokensDark.colorInfo,
    borderRadius: kitTokensDark.borderRadius,
    borderRadiusLG: 8,
    borderRadiusSM: 4,
    fontSize: kitTokensDark.fontSize,
    fontFamily: kitTokensDark.fontFamily,
    colorBgLayout: kitTokensDark.colorBgLayout,
    colorBgContainer: kitTokensDark.colorBgContainer,
    colorBgElevated: '#27272a',
    /** Tooltip / popover spotlight — not derived from inverted white primary */
    colorBgSpotlight: '#27272a',
    colorText: kitTokensDark.colorTextBase,
    colorTextSecondary: '#a1a1aa',
    colorTextTertiary: '#71717a',
    colorBorder: kitTokensDark.colorBorder,
    colorBorderSecondary: '#27272a',
    colorTextLightSolid: kitTokensDark.colorTextLightSolid,
    /** List hover/active: zinc, never white-on-white from colorPrimary */
    controlItemBgHover: '#27272a',
    controlItemBgActive: '#3f3f46',
    controlHeight: kitTokensDark.controlHeight,
    motionDurationMid: '0.18s',
    motionEaseOut: 'cubic-bezier(0.2, 0.8, 0.2, 1)',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.4)',
    boxShadowSecondary: '0 16px 48px rgba(0, 0, 0, 0.5)',
  },
  components: {
    Layout: {
      bodyBg: '#09090b',
      headerBg: '#09090b',
      siderBg: '#09090b',
      headerHeight: 48,
    },
    Menu: {
      itemBorderRadius: 6,
      itemSelectedBg: '#27272a',
      itemSelectedColor: '#fafafa',
      itemHoverBg: '#27272a',
      itemHoverColor: '#fafafa',
    },
    Table: {
      headerBg: 'transparent',
      rowHoverBg: '#27272a',
      borderColor: '#27272a',
      cellPaddingBlock: 10,
    },
    Input: {
      activeBorderColor: '#a1a1aa',
      hoverBorderColor: '#71717a',
      activeShadow: 'none',
    },
    Select: {
      optionSelectedBg: '#3f3f46',
      optionActiveBg: '#27272a',
    },
    Dropdown: {
      controlItemBgHover: '#27272a',
      controlItemBgActive: '#3f3f46',
    },
    Segmented: {
      itemSelectedBg: '#fafafa',
      itemSelectedColor: '#18181b',
      trackBg: '#18181b',
    },
    Pagination: {
      itemActiveBg: '#fafafa',
      itemBg: 'transparent',
      itemLinkBg: 'transparent',
      itemInputBg: '#27272a',
    },
    /** Force light tooltip text — global colorTextLightSolid is dark for white buttons */
    Tooltip: {
      colorBgSpotlight: '#27272a',
      colorTextLightSolid: '#fafafa',
    },
    ...sharedComponents,
    Modal: {
      ...sharedComponents.Modal,
      contentBg: '#18181b',
      headerBg: '#18181b',
    },
  },
})

export function getKitTheme(mode: 'light' | 'dark'): ThemeConfig {
  return mode === 'dark' ? kitThemeDark : kitTheme
}

export function getKitTokens(mode: 'light' | 'dark') {
  return mode === 'dark' ? kitTokensDark : kitTokens
}
